import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Badge, Tooltip } from 'flowbite-react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useTask } from '../../hooks/useTask';
import RemoteReferenceIcon from '../../components/RemoteReferenceIcon';
import EditableTitle from '../../components/EditableTitle';
import DependencyViewer from '../../components/dependencies/DependencyViewer';
import WorkEstimateForm from '../../components/forms/WorkEstimateForm';
import { ScheduleWarningsListEntry } from '../../components/schedule/ScheduleWarningsList';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import WorkMatchPairForm from '../../components/schedule/WorkMatchPairForm';
import WorkMatchList from '../../components/schedule/WorkMatchList';
import Avatar from '../../components/common/Avatar';
import { useAsync } from 'react-async-hook';

import {
  FiCheckCircle,
  FiCircle,
  FiFolder,
  FiTrash2,
  FiFileText,
  FiBarChart2,
  FiUsers,
  FiGitBranch,
  FiAlertTriangle,
  FiTarget,
  FiPlus,
} from 'react-icons/fi';
import TypeBadge from '@/components/common/TypeBadge';
import ImprovementHelper from '@/components/common/ImprovementHelper';
import WatchButton from '@/components/common/WatchButton';

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { task, loading, error } = useTask(taskId);
  const { applyCommand, workspaceId, database } = useDatabase();
  const { safeData, getUserProfile } = useContext(WorkspaceContext);
  const [warningText, setWarningText] = useState<string | undefined>(undefined);

  // Deadline form state
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineDescription, setDeadlineDescription] = useState('');

  useEffect(() => {
    if (!safeData || !taskId) return;
    let active = true;
    const cb = async (fuzzyResult: any) => {
      if (!active || !fuzzyResult) return;
      try {
        const meta = fuzzyResult.meta;
        const unschedulable = fuzzyResult.schedule?.unschedulable ?? {};
        for (const [nodeId, entity] of Object.entries(meta?.node_to_entity ?? {})) {
          if ((entity as any)?.type === 'Task' && (entity as any)?.id === taskId) {
            const item = unschedulable[nodeId] as any;
            if (item) {
              setWarningText(item.reason);
              return;
            }
          }
        }
        setWarningText(undefined);
      } catch {
        setWarningText(undefined);
      }
    };
    wasm.DataManager.select_schedule(safeData, cb);
    return () => {
      active = false;
      wasm.DataManager.unsubscribe(safeData, cb);
    };
  }, [safeData, taskId]);

  const updateTaskTitle = async (newTitle: string) => {
    if (!taskId || !task) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTask',
      id: taskId,
      actions: [{ type: 'SetTitle', title: newTitle }],
    };
    await applyCommand(command);
  };

  const toggleTaskCompletion = async () => {
    if (!taskId || !task) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTask',
      id: taskId,
      actions: [{ type: 'SetCompleted', completed: !task.complete }],
    };
    await applyCommand(command);
  };

  const addDeadline = async () => {
    if (!taskId || !deadlineDate) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTask',
      id: taskId,
      actions: [
        {
          type: 'InsertDeadline',
          deadline: {
            date: deadlineDate,
            description: deadlineDescription || '',
          },
        },
      ],
    };
    await applyCommand(command);
    setDeadlineDate('');
    setDeadlineDescription('');
  };

  const deleteDeadline = async (deadlineId: string) => {
    if (!taskId) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTask',
      id: taskId,
      actions: [{ type: 'DeleteDeadline', id: deadlineId }],
    };
    await applyCommand(command);
  };

  // Inline component for a single estimate row
  const EstimateRow: React.FC<{
    estimate: any;
    index: number;
    onDelete: () => void;
  }> = ({ estimate, index, onDelete }) => {
    const creator = estimate.annotation?.creator;
    const isUser = creator?.type === 'User';
    const userId: string | undefined = isUser ? creator.id : undefined;

    const profileResult = useAsync(async () => {
      if (!userId) return undefined;
      return await getUserProfile(userId);
    }, [userId]);

    return (
      <div
        key={estimate.id || index}
        className="py-3 grid grid-cols-[1fr_auto_auto] items-center gap-2"
      >
        <div>
          <div className="flex items-center gap-2">
            {isUser ? (
              <>
                <Avatar
                  imageUrl={profileResult.result?.avatar_url || undefined}
                  name={profileResult.result?.display_name || userId!}
                  size="sm"
                />
                <span className="font-medium text-cm-text-primary">
                  {profileResult.result?.display_name || userId}
                </span>
              </>
            ) : (
              <span className="font-medium text-cm-text-primary">
                {creator ? 'External Source' : 'Anonymous'}
              </span>
            )}
          </div>
          {estimate.annotation?.reason && (
            <p className="text-sm text-cm-text-muted mt-1">
              {estimate.annotation.reason}
            </p>
          )}
          {estimate.annotation?.updated_at && (
            <p className="text-xs text-cm-text-muted mt-1">
              {new Date(estimate.annotation.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="text-right">
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-cm-accent-blue/15 text-cm-accent-blue">
            {estimate.value}
          </span>
        </div>
        <div>
          <button
            className="text-cm-text-muted hover:text-red-400 cursor-pointer p-1 rounded-full hover:bg-cm-bg-hover transition-colors"
            onClick={onDelete}
            aria-label="Delete estimate"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
        Loading task...
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="p-6">
        <div className="p-4 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
          {error || 'Task not found'}
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <EditableTitle
            title={task.title}
            onTitleChange={updateTaskTitle}
            size="lg"
            className="flex-grow"
          />
          <ImprovementHelper
            entityType="task"
            entityName={task.title}
            target={taskId ? { kind: 'Task', id: taskId } : undefined}
          />
          {taskId && (
            <WatchButton
              workspaceId={workspaceId}
              target={{ type: 'task', id: taskId }}
              showLabel={false}
            />
          )}
          <RemoteReferenceIcon
            reference={task.remoteReference}
            showLabel={true}
            className="ml-2"
          />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <TypeBadge t="Task" />
          <button
            onClick={toggleTaskCompletion}
            className="flex items-center gap-1.5 text-sm text-cm-text-secondary hover:text-cm-text-primary transition-colors"
            aria-label={
              task.complete ? 'Mark as incomplete' : 'Mark as complete'
            }
          >
            {task.complete ? (
              <FiCheckCircle className="w-4 h-4 text-cm-accent-green" />
            ) : (
              <FiCircle className="w-4 h-4" />
            )}
            <span>{task.complete ? 'Completed' : 'In Progress'}</span>
          </button>
          {task.project && (
            <Link
              to={`/workspaces/${workspaceId}/projects/${task.project.id}`}
              className="flex items-center gap-1.5 text-sm text-cm-text-muted hover:text-cm-accent-orange transition-colors"
            >
              <FiFolder className="w-3.5 h-3.5" />
              {task.project.name}
            </Link>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiFileText className="w-5 h-5 text-cm-accent-blue" />
          <h3 className="font-mono font-medium text-cm-text-primary">
            Description
          </h3>
        </div>
        <div className="prose dark:prose-invert max-w-none text-sm">
          {task.description ? (
            <ReactMarkdown>{task.description}</ReactMarkdown>
          ) : (
            <p className="text-cm-text-muted italic">
              No description provided
            </p>
          )}
        </div>
      </div>

      {/* Deadlines */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiTarget className="w-5 h-5 text-cm-accent-orange" />
          <h3 className="font-mono font-medium text-cm-text-primary">
            Deadlines
          </h3>
        </div>

        {task.deadlines && task.deadlines.length > 0 ? (
          <div className="divide-y divide-cm-border-subtle mb-4">
            {task.deadlines
              .slice()
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((deadline, index) => {
                const isPast = deadline.date < today;
                return (
                  <div
                    key={deadline.id || index}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`text-sm font-mono ${isPast ? 'text-red-400' : 'text-cm-text-secondary'}`}
                      >
                        {new Date(
                          deadline.date + 'T12:00:00',
                        ).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      {deadline.description && (
                        <span className="text-sm text-cm-text-primary truncate">
                          {deadline.description}
                        </span>
                      )}
                      {isPast && (
                        <span className="text-xs text-red-400 font-medium">
                          overdue
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteDeadline(deadline.id)}
                      className="text-cm-text-muted hover:text-red-400 p-1 rounded-full hover:bg-cm-bg-hover transition-colors shrink-0"
                      aria-label="Delete deadline"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
          </div>
        ) : (
          <p className="text-sm text-cm-text-muted text-center py-4 mb-4">
            No deadlines set.
          </p>
        )}

        {/* Add deadline form */}
        <div className="flex items-end gap-3 pt-3 border-t border-cm-border-subtle">
          <div className="flex-shrink-0">
            <label className="block text-xs text-cm-text-muted mb-1">
              Date
            </label>
            <input
              type="date"
              value={deadlineDate}
              onChange={(e) => setDeadlineDate(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-cm-border-subtle bg-cm-bg-deep text-sm text-cm-text-primary focus:outline-none focus:border-cm-accent-orange"
            />
          </div>
          <div className="flex-grow">
            <label className="block text-xs text-cm-text-muted mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={deadlineDescription}
              onChange={(e) => setDeadlineDescription(e.target.value)}
              placeholder="e.g. Beta release, Sprint end"
              className="w-full px-3 py-1.5 rounded-lg border border-cm-border-subtle bg-cm-bg-deep text-sm text-cm-text-primary placeholder:text-cm-text-muted focus:outline-none focus:border-cm-accent-orange"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deadlineDate) addDeadline();
              }}
            />
          </div>
          <button
            onClick={addDeadline}
            disabled={!deadlineDate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cm-accent-orange text-cm-bg-deep text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Estimates */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiBarChart2 className="w-5 h-5 text-cm-accent-blue" />
          <h3 className="font-mono font-medium text-cm-text-primary">
            Estimates
          </h3>
        </div>

        {task.estimates && task.estimates.length > 0 ? (
          <>
            <div className="grid grid-cols-[1fr_auto_auto] mb-2 pb-2 border-b border-cm-border-subtle">
              <div className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">
                Source
              </div>
              <div className="text-right text-xs font-medium text-cm-text-muted uppercase tracking-wider">
                Estimate
              </div>
              <div></div>
            </div>
            <div className="divide-y divide-cm-border-subtle">
              {task.estimates
                .slice()
                .sort((a, b) => {
                  if (a.value !== b.value) return a.value - b.value;
                  if (
                    a.annotation?.updated_at &&
                    b.annotation?.updated_at
                  ) {
                    return (
                      new Date(b.annotation.updated_at).getTime() -
                      new Date(a.annotation.updated_at).getTime()
                    );
                  }
                  return 0;
                })
                .map((estimate, index) => (
                  <EstimateRow
                    key={estimate.id || index}
                    estimate={estimate}
                    index={index}
                    onDelete={() => {
                      if (estimate.id && taskId) {
                        const command: wasm.DatabaseCommand = {
                          type: 'UpdateTask',
                          id: taskId,
                          actions: [
                            { type: 'DeleteEstimate', id: estimate.id },
                          ],
                        };
                        applyCommand(command);
                      }
                    }}
                  />
                ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-cm-text-muted text-center py-4">
            No estimates yet. Add one below.
          </p>
        )}

        <div className="border-t border-cm-border-subtle pt-4 mt-4">
          {taskId && (
            <WorkEstimateForm
              taskId={taskId}
              onSubmit={async (command) => {
                await applyCommand(command);
              }}
            />
          )}
        </div>
      </div>

      {/* Work Matching */}
      {taskId && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiUsers className="w-5 h-5 text-cm-accent-green" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Work Matching
            </h3>
          </div>
          <p className="text-sm text-cm-text-muted mb-3">
            Set individual work assignments or general work matches to enable
            automatic scheduling.
          </p>
          <WorkMatchList
            type="dependency"
            dependency={{ kind: 'Task', id: taskId }}
          />
          <WorkMatchPairForm
            context={{
              kind: 'Task',
              id: taskId,
              projectId: task.project?.id ?? null,
            }}
          />
        </div>
      )}

      {/* Dependencies & Schedule */}
      {taskId && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiGitBranch className="w-5 h-5 text-cm-accent-purple" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Dependencies & Schedule
            </h3>
          </div>
          <DependencyViewer nodeId={{ Task: taskId }} height="500px" />
        </div>
      )}

      {/* Scheduling warnings */}
      {taskId && warningText && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiAlertTriangle className="w-5 h-5 text-cm-accent-orange" />
            <h3 className="font-mono font-medium text-cm-text-primary">
              Scheduling
            </h3>
          </div>
          <ScheduleWarningsListEntry
            taskOrOutcome={{ type: 'task', id: taskId }}
          />
        </div>
      )}
    </div>
  );
};

export default TaskDetailPage;
